/**
 * AtlasPerfHudModule — the G2 draw-call/FPS overlay (real-hardware observability).
 *
 * Reuses the engine's DOM PerfHUD (no extra dependency). Each UI-phase frame it counts
 * the VISIBLE draw-bearing objects in the scene — every Mesh, InstancedMesh (1 call for
 * all its instances), and BatchedMesh is one draw call — so the readout is accurate on
 * real WebGPU hardware AND under the headless WebGL2 fallback (where renderer.info's
 * per-draw counters read 0). Triangles count the active instance count per InstancedMesh.
 *
 * GPU frame time (wave 2): three r182's built-in renderer timestamps
 * (`trackTimestamp` + `resolveTimestampsAsync` → `info.render.timestamp`) feed the
 * PerfHUD's GpuProfiler section through a tiny adapter. This is the measurement the
 * deep-research pass couldn't source publicly — real per-frame GPU ms on OUR hardware,
 * so the post stack (bloom/SSAO/DoF/clouds via the Esc graphics menu) can be tuned by
 * toggling an effect and watching the number. Requires the WebGPU 'timestamp-query'
 * feature (the engine's RuntimeCaps already requests it when the adapter offers it);
 * degrades to the HUD's built-in "unavailable" line otherwise.
 *
 * The headless harness asserts the draw-call ceiling directly (verify:drawcalls); this
 * HUD is for watching FPS + the live draw-call budget on real hardware. Toggle with F3.
 */
import * as THREE from 'three/webgpu';
import { settleReports, settleLine } from '../lib/settle_hotspots.js';
import { lod0Ledger } from '../lib/lod0_ledger.js';
import type { App, IModule } from 'enginex';
import { PerfHUD } from 'enginex/ui/PerfHUD.js';
import type { GpuProfiler, PassStats } from 'enginex/debug/gpuprofiler/GpuProfiler.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { capsIn } from '../lib/world_caps.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';

/** The subset of the three r182 WebGPURenderer timestamp API we touch (backend-agnostic). */
interface TimestampRenderer {
  trackTimestamp?: boolean;
  resolveTimestampsAsync?: (type?: unknown) => Promise<unknown>;
  info?: { render?: { timestamp?: number } };
  backend?: { trackTimestamp?: boolean; hasFeature?: (name: string) => boolean };
}

export class AtlasPerfHudModule implements IModule {
  readonly name = 'AtlasPerfHud';
  private hud: PerfHUD | null = null;
  private gateTimer: number | null = null;
  private app!: App;
  private native: TimestampRenderer | null = null;
  private gpuHistory: number[] = [];
  private gpuAvailable = false;
  private resolving = false;
  private tsProbed = false;
  /** Rate limit for the drawCallBudget warn (init −30 s so the FIRST breach warns immediately). */
  private lastBudgetWarnMs = -30_000;
  private tsSupported = false;
  private censusTick = 14; private censusDraws = 0; private censusTris = 0;
  /** Admin gate: the HUD (and its F3 toggle) is an ADMIN diagnostic — hidden
   *  for regular players and always hidden on the boot main menu. */
  private allowed = false;
  private autoShown = false;
  private onKey = (e: KeyboardEvent): void => { if (e.key === 'F3' && this.allowed) this.hud?.toggle(); };

  init(app: App): void {
    this.app = app;
    this.hud = new PerfHUD();
    window.addEventListener('keydown', this.onKey);
    // Visibility: only once IN a world (never over the main menu / loading
    // screen) and only when the player holds the admin cap there. Caps
    // replicate asynchronously, so re-evaluate on a slow poll + world hops.
    const evalGate = (): void => {
      const inWorld = !atlas.loadingGate && atlas.currentWorldId !== null;
      const was = this.allowed;
      this.allowed = inWorld && capsIn(atlas.currentWorldId).admin;
      if (was && !this.allowed) this.hud?.hide();
      if (this.allowed && !this.autoShown) { this.autoShown = true; this.hud?.show(); }
    };
    this.gateTimer = window.setInterval(evalGate, 1500);
    bus.on('atlas:loading-done', evalGate);
    bus.on('atlas:world-entered', () => { window.setTimeout(evalGate, 500); });

    // GPU timestamps are enabled lazily in update(): the BACKEND copies
    // trackTimestamp at its init (renderer-flag flips after that are ignored,
    // and resolveTimestampsAsync then warns "Timestamp tracking is disabled"
    // every session), so we probe backend.hasFeature once it exists and flip
    // BOTH flags — or skip resolving entirely when the device can't do it.
    this.native = app.renderer.native as unknown as TimestampRenderer;

    // Adapter: the PerfHUD renders any GpuProfiler-shaped source; we feed it one
    // "GPU frame" scope backed by the renderer's resolved frame timestamp ring.
    const self = this;
    const adapter = {
      get available(): boolean { return self.gpuAvailable; },
      getFrameStats(): Map<string, PassStats> {
        const h = self.gpuHistory;
        if (h.length === 0) return new Map();
        let min = Infinity, max = 0, sum = 0;
        for (const v of h) { if (v < min) min = v; if (v > max) max = v; sum += v; }
        return new Map([['GPU frame', {
          meanMs: sum / h.length, maxMs: max, minMs: min, lastMs: h[h.length - 1], history: [...h],
        }]]);
      },
    };
    this.hud.setGpuProfiler(adapter as unknown as GpuProfiler);

    // Always-on diagnostics: `window.__drawCensus()` in the console groups every
    // visible drawable by name/owner — the tool that names a draw-item spike
    // (three r182 WebGPU pays per-object UBO cost, so the COUNT is the frame).
    // ── Which mesh blew the vertex-buffer limit? ──────────────────────────
    // WebGPU allows 8 vertex buffers (`maxVertexBuffers`), and three builds one
    // per attribute the SHADER READS (`renderObject.getAttributes()`). Over the
    // limit, pipeline creation fails and the console repeats
    // `[Invalid RenderPipeline "renderPipeline_<name|type>_<id>"]` every frame
    // without ever naming the object (2026-09-15).
    //
    // The label carries the material's id, so: `__findMaterial(105)` walks the
    // scene and names the mesh, its geometry's attributes and its instancing.
    // With no argument it lists every geometry at or near the limit.
    (window as unknown as Record<string, unknown>).__findMaterial = (id?: number): unknown => {
      const hits: unknown[] = [];
      this.app.world.scene.traverse((o) => {
        const m = o as THREE.Mesh & { isMesh?: boolean; isInstancedMesh?: boolean; isSkinnedMesh?: boolean; isPoints?: boolean; isLine?: boolean; count?: number };
        if (!(m.isMesh || m.isInstancedMesh || m.isPoints || m.isLine)) return;
        const geo = m.geometry as THREE.BufferGeometry | undefined;
        if (!geo) return;
        const mats = (Array.isArray(m.material) ? m.material : [m.material]) as THREE.Material[];
        for (const mat0 of mats) {
          if (!mat0) continue;
          // three's Material carries a numeric `id`; the bundled types omit it.
          const mat = mat0 as THREE.Material & { id: number };
          const attrs = Object.keys(geo.attributes ?? {});
          // What three would bind: every geometry attribute, plus the instance
          // matrix (and its PREVIOUS copy when the MRT carries velocity) and an
          // instance colour when present.
          const inst = m.isInstancedMesh ? 1 + ((m as THREE.InstancedMesh).instanceColor ? 1 : 0) : 0;
          const estimate = attrs.length + inst;
          const match = id === undefined ? estimate >= 8 : mat.id === id;
          if (!match) continue;
          let path = m.name; let p: THREE.Object3D | null = m.parent;
          while (p) { if (p.name) path = `${p.name}/${path}`; p = p.parent; }
          hits.push({
            materialId: mat.id, materialType: mat.type, materialName: mat.name || '(unnamed)',
            label: `renderPipeline_${mat.name || mat.type}_${mat.id}`,
            object: path || '(unnamed)', instanced: !!m.isInstancedMesh, skinned: !!m.isSkinnedMesh,
            instances: m.count ?? 1,
            attributeCount: attrs.length, attributes: attrs,
            estimatedVertexBuffers: estimate,
            overLimit: estimate > 8,
          });
        }
      });
      return hits.length ? hits : `no ${id === undefined ? 'geometry at or over the 8-buffer limit' : `material with id ${id}`} in the scene graph — it may belong to a post pass or an off-scene render`;
    };

    (window as unknown as Record<string, unknown>).__drawCensus = (): unknown => {
      const groups = new Map<string, { n: number; inst: number; tris: number }>();
      let total = 0;
      this.app.world.scene.traverseVisible((o) => {
        const m = o as THREE.Mesh & { isInstancedMesh?: boolean; isBatchedMesh?: boolean; isSprite?: boolean; count?: number };
        if (!(m.isMesh || m.isInstancedMesh || m.isBatchedMesh || m.isSprite)) return;
        total++;
        let n = m.name; let p: THREE.Object3D | null = m.parent;
        while (!n && p) { n = p.name; p = p.parent; }
        if (!n) {
          const mat = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material | undefined;
          n = `unnamed(${(m.geometry as THREE.BufferGeometry | undefined)?.type ?? '?'}/${mat?.type ?? '?'})`;
        }
        const key = `${m.type}:${n.replace(/[0-9a-f]{8,}/g, 'HASH').replace(/[0-9]+/g, '#').slice(0, 52)}`;
        const g = groups.get(key) ?? { n: 0, inst: 0, tris: 0 };
        g.n++;
        g.inst += m.isInstancedMesh ? (m.count ?? 0) : 1;
        const geo = m.geometry as THREE.BufferGeometry | undefined;
        if (geo) g.tris += Math.round(((geo.index ? geo.index.count : geo.attributes.position?.count ?? 0) / 3) * (m.isInstancedMesh ? (m.count ?? 0) : 1));
        groups.set(key, g);
      });
      const rows = [...groups.entries()].sort((a, b) => b[1].n - a[1].n)
        // TRIANGLES PER INSTANCE is the column that names an oversized ASSET
        // rather than an oversized scene (2026-09-15): 151 foliage instances at
        // 2.97 M triangles reads as "a lot of foliage" until you divide and get
        // 19.6 k each, which is a cooked-asset problem, not a streaming one.
        .map(([k, g]) => `${String(g.n).padStart(5)} obj ${String(g.inst).padStart(7)} inst ${String(Math.round(g.tris / 1000)).padStart(8)}k tri ${String(g.inst > 0 ? Math.round(g.tris / g.inst) : g.tris).padStart(7)}/inst  ${k}`);
      console.log(`__drawCensus: ${total} drawables\n` + rows.slice(0, 30).join('\n'));
      return { total, rows };
    };

    app.world.addSystem(makeSystem('AtlasPerfHud', Phase.UI, 90, (dt) => this.update(dt)));
  }

  /** R10: the settle report as a first-class HUD row (last settlement's per-stage times). */
  private settleRow: HTMLDivElement | null = null;
  private settleText = '';
  private updateSettleRow(): void {
    const container = (this.hud as unknown as { container?: HTMLElement }).container;
    if (!container) return;
    if (!this.settleRow) {
      this.settleRow = document.createElement('div');
      this.settleRow.style.cssText = 'margin-top:4px;padding-top:4px;border-top:1px solid rgba(255,255,255,0.15);white-space:pre-wrap;max-width:420px;color:#9fd;';
      container.appendChild(this.settleRow);
    }
    const text = `${settleLine(settleReports.last)}\n${lod0Ledger.line()}`; // L9: the refinement's swap ledger under the settle line
    if (text !== this.settleText) { this.settleText = text; this.settleRow.textContent = text; }
  }

  private update(dt: number): void {
    if (!this.hud) return;
    // The draw census walks EVERY scene node (settlements, flora, instanced
    // pools — thousands) each frame; PerfHUD.update discards it while hidden,
    // and the overlay is admin-gated + hidden by default — every player paid
    // a full scene traversal per frame for nothing. Walk only while visible
    // (an unknown flag shape falls back to the old always-on behaviour). The
    // GPU-timestamp drain below must keep running regardless — it empties the
    // compute query pool ("can't move while it snows").
    const hudVisible = (this.hud as unknown as { visible?: boolean }).visible !== false;
    if (hudVisible) {
      // The census is a full scene walk (thousands of nodes): 4 Hz is plenty
      // for a readout; the frame time still updates every frame (2026-09-12).
      let draws = this.censusDraws, tris = this.censusTris;
      if (++this.censusTick >= 15) {
        this.censusTick = 0;
        draws = 0; tris = 0;
        this.app.world.scene.traverseVisible((o) => {
        const m = o as THREE.Mesh & { isInstancedMesh?: boolean; isBatchedMesh?: boolean; count?: number };
        if (!(m.isMesh || m.isInstancedMesh || m.isBatchedMesh)) return;
        draws++; // one draw call per drawable object (an InstancedMesh batches all instances)
        const g = m.geometry as THREE.BufferGeometry | undefined;
        if (g) { const t = (g.index ? g.index.count : g.attributes.position?.count ?? 0) / 3; tris += t * (m.isInstancedMesh ? (m.count ?? 0) : 1); }
        });
        this.censusDraws = draws; this.censusTris = tris;
      }
      this.hud.update({ frameTime: dt * 1000, drawCalls: draws, triangles: Math.round(tris), entities: draws });
      this.updateSettleRow();

      // drawCallBudget is the preset's draw-item ceiling (three r182 WebGPU pays
      // per-object UBO cost — the COUNT is the frame). It had NO consumer until
      // this warn (finding F7: a dormant knob documented as "HUD warn threshold").
      const budget = activePreset().drawCallBudget;
      if (budget > 0 && draws > budget && performance.now() - this.lastBudgetWarnMs > 30_000) {
        this.lastBudgetWarnMs = performance.now();
        console.warn(`[perf] visible drawables ${draws} exceed the '${activeTier()}' tier budget ${budget} — run __drawCensus() to name the spike`);
      }
    }

    // One-time capability probe once the backend exists (it appears after the
    // renderer's async init, which can post-date module init).
    if (!this.tsProbed && this.native?.backend) {
      this.tsProbed = true;
      try {
        const backend = this.native.backend;
        // WebGPU device feature / WebGL2 timer-query extension.
        this.tsSupported = backend.hasFeature?.('timestamp-query') === true
          || backend.hasFeature?.('EXT_disjoint_timer_query_webgl2') === true;
        if (this.tsSupported) {
          backend.trackTimestamp = true;
          this.native.trackTimestamp = true;
        }
      } catch { this.tsSupported = false; }
    }

    // Resolve last frame's GPU timestamps (async readback; one in flight at a time).
    if (this.tsSupported && !this.resolving && this.native?.resolveTimestampsAsync) {
      const tq = (THREE as unknown as { TimestampQuery?: { RENDER?: unknown; COMPUTE?: unknown } }).TimestampQuery;
      const p = this.native.resolveTimestampsAsync(tq?.RENDER);
      if (p && typeof p.then === 'function') {
        this.resolving = true;
        p.then(() => {
          const ms = this.native?.info?.render?.timestamp;
          if (typeof ms === 'number' && ms > 0) {
            this.gpuAvailable = true;
            this.gpuHistory.push(ms);
            if (this.gpuHistory.length > 60) this.gpuHistory.shift();
          }
          // Drain the COMPUTE pool too: with trackTimestamp on, every GPU
          // compute dispatch (snow/rain particles, GPU sims) allocates compute
          // queries — resolving only RENDER let that pool EXHAUST and stall
          // the whole frame ("can't move while it snows", 2026-07-20).
          if (tq?.COMPUTE !== undefined) return this.native?.resolveTimestampsAsync?.(tq.COMPUTE);
        }).catch(() => { /* timestamp-query absent or readback failed — HUD shows unavailable */ })
          .finally(() => { this.resolving = false; });
      }
    }
  }

  dispose(): void {
    if (this.gateTimer !== null) { window.clearInterval(this.gateTimer); this.gateTimer = null; }
    window.removeEventListener('keydown', this.onKey);
    this.hud?.dispose();
  }
}
